<script setup>
import { computed, reactive } from 'vue';
import PieChartOptions from '~/utils/chartConfigs/pieChart.js';
import { addThousandsSeparator } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props
const props = defineProps({
    currentMinedPercentage: { type: Number, default: 0 },
    sixMonthMinedPercentage: { type: Number, default: 0 },
    oneYearMinedPercentage: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
});

// Chart options
const chartOptions = reactive({
    ...PieChartOptions,
    dataLabels: { enabled: false },
    legend: { show: true, position: 'bottom', labels: { colors: '#9ca3af' } },
    labels: ['Mined', 'Remaining'],
    colors: ['#60A5FA', '#BFDBFE'],
    tooltip: { enabled: false },
    chart: {
        events: {
            mouseMove: () => false,
            click: () => false
        }
    },
    states: {
        hover: { filter: { type: 'none' } },
        active: { filter: { type: 'none' } }
    },
    plotOptions: {
        pie: {
            expandOnClick: false
        }
    }
});

// Current percentage of supply mined
const currentChartSeries = computed(() => [
    parseFloat(props.currentMinedPercentage),
    100 - parseFloat(props.currentMinedPercentage)
]);

// 6 month percentage of supply mined
const sixMonthChartSeries = computed(() => [
    parseFloat(props.sixMonthMinedPercentage),
    100 - parseFloat(props.sixMonthMinedPercentage)
]);

// 1 year percentage of supply mined
const oneYearChartSeries = computed(() => [
    parseFloat(props.oneYearMinedPercentage),
    100 - parseFloat(props.oneYearMinedPercentage)
]);
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-3 cards-gap">
        <!-- Current Supply -->
        <Card>
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
                <!-- Section Title -->
                <div class="flex items-center w-full sm:w-auto">
                    <CardLabel text="Current Supply" />
                    <InfoTooltip text="The amount of supply that has been mined as of now" />
                </div>
            </div>

            <!-- Chart Container with Fixed Height -->
            <div class="h-[250px]">
                <LoadingSkeleton v-if="props.loading" height="100%" width="100%" />
                <div v-else class="slide-in-up">
                    <ClientOnly>
                        <div class="relative">
                            <VueApexCharts type="donut" height="250" :options="chartOptions" :series="currentChartSeries" />
                            <div class="absolute top-[112px] left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                                <span class="text-xl font-bold text-gray-900 dark:text-white me-1">{{ addThousandsSeparator(props.currentMinedPercentage, 2) }}</span>
                                <small class="text-sm dark:text-white">%</small>
                                <p class="text-xs text-gray-500 dark:text-gray-400">Mined</p>
                            </div>
                        </div>
                    </ClientOnly>
                </div>
            </div>
        </Card>

        <Card>
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
                <div class="flex items-center w-full sm:w-auto">
                    <CardLabel text="6 Month Projection" />
                    <InfoTooltip text="Estimated supply that will be mined 6 months from now" />
                </div>
            </div>

            <!-- Chart Container with Fixed Height -->
            <div class="h-[250px]">
                <LoadingSkeleton v-if="props.loading" height="100%" width="100%" />
                <div v-else class="slide-in-up">
                    <ClientOnly>
                        <div class="relative">
                            <VueApexCharts type="donut" height="250" :options="chartOptions" :series="sixMonthChartSeries" />
                            <div class="absolute top-[112px] left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                                <span class="text-xl font-bold text-gray-900 dark:text-white me-1">{{ addThousandsSeparator(props.sixMonthMinedPercentage, 2) }}</span>
                                <small class="text-sm dark:text-white">%</small>
                                <p class="text-xs text-gray-500 dark:text-gray-400">Mined</p>
                            </div>
                        </div>
                    </ClientOnly>
                </div>
            </div>
        </Card>

        <!-- 1 Year Projection -->
        <Card>
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
                <div class="flex items-center w-full sm:w-auto">
                    <CardLabel text="1 Year Projection" />
                    <InfoTooltip text="Estimated supply that will be mined 1 year from now" />
                </div>
            </div>

            <!-- Chart Container with Fixed Height -->
            <div class="h-[250px]">
                <LoadingSkeleton v-if="props.loading" height="100%" width="100%" />
                <div v-else class="slide-in-up">
                    <ClientOnly>
                        <div class="relative">
                            <VueApexCharts type="donut" height="250" :options="chartOptions" :series="oneYearChartSeries" />
                            <div class="absolute top-[112px] left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                                <span class="text-xl font-bold text-gray-900 dark:text-white me-1">{{ addThousandsSeparator(props.oneYearMinedPercentage, 2) }}</span>
                                <small class="text-sm dark:text-white">%</small>
                                <p class="text-xs text-gray-500 dark:text-gray-400">Mined</p>
                            </div>
                        </div>
                    </ClientOnly>
                </div>
            </div>
        </Card>
    </div>
</template>